import { forwardRef, ReactNode } from "react"

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  name?: string
  options: SelectOption[]
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  placeholder?: string
  showPlaceholder?: boolean
  disabled?: boolean
  hasError?: boolean
  multiple?: boolean
  className?: string
  children?: ReactNode
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      name,
      options,
      value,
      onChange,
      placeholder = "選択してください",
      showPlaceholder = true,
      disabled = false,
      hasError = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const baseClasses = `py-[15px] px-3 pr-10 border border-medium-light-gray rounded-md bg-white font-noto font-normal text-[1.4rem] leading-[1.5]
    text-smooth-black cursor-pointer focus:outline-none focus:border-smooth-blue appearance-none`

    const borderClasses = hasError ? "border-error" : "border-smooth-gray"
    const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : ""

    const combinedClasses = `${baseClasses} ${borderClasses} ${disabledClasses} ${className}`.trim()

    return (
      <div className="relative w-fit">
        <select
          name={name}
          ref={ref}
          className={combinedClasses}
          disabled={disabled}
          value={value}
          onChange={onChange}
          {...props}
        >
          {showPlaceholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* 下向き三角形のアイコン */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    )
  }
)

Select.displayName = "Select"

export default Select
